'use client'

import type { Table as TanstackTable } from '@tanstack/react-table'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'

import { NO_SCHOOL_CLASS_ID } from '@package/api/schemas'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@ui/web/components'
import { SCHOOL_GRADE_OPTIONS, schoolGradeLabel } from '@helper/utils/domain'

import type { AccessFilter, Student, StudentFilterId } from './types'
import { SchoolClassAvatar } from '../../components/school-class-avatar'
import { useAuthClient, useSharedTRPC } from '../../context'
import { CLASS_FILTER_COLUMN, GRADE_FILTER_COLUMN, NO_SCHOOL_GRADE_ID } from './types'

const ALL_VALUE = 'all'

const ACCESS_LABEL: Record<AccessFilter, string> = {
  limited: 'Terbatas',
  premium: 'Premium',
}

export interface ToolbarProps {
  table: TanstackTable<Student>
  /** Selects to render, in order. An empty list leaves just the name search. */
  filters: StudentFilterId[]
  searchPlaceholder: string
  /** Bulk-action bar, rendered to the right of the search once rows are selected. */
  actions?: ReactNode
}

/**
 * Filters and name search for a student roster.
 *
 * Lives above the table and reads or writes the TanStack table instance directly — including the
 * class and grade filters, which a host page may then mirror into the URL by controlling
 * `columnFilters` on `StudentTable`.
 */
export function Toolbar({ table, filters, searchPlaceholder, actions }: ToolbarProps) {
  const trpc = useSharedTRPC()
  const authClient = useAuthClient()
  const showClassFilter = filters.includes('schoolClass')

  // The class list is role-aware, mirroring `SchoolClassSelect`: owner/admin pick from every class
  // in the school, a member only from the classes they are assigned to — offering a member a class
  // they cannot open would filter the roster down to nothing.
  const { data: memberRole, isPending: isRolePending } = authClient.useActiveMemberRole()
  const isAdminOrOwner = memberRole?.role === 'owner' || memberRole?.role === 'admin'

  // Only the class filter needs the class list; skip the request on the rosters that do not render
  // it (the class page is already scoped to one class).
  const allTeamsQuery = useQuery({
    ...trpc.myOrganization.getAllTeams.queryOptions(),
    enabled: showClassFilter && isAdminOrOwner,
  })
  const myTeamsQuery = useQuery({
    ...trpc.myOrganization.getMyTeams.queryOptions(),
    enabled: showClassFilter && !isAdminOrOwner && !isRolePending,
  })

  const classes = (isAdminOrOwner ? allTeamsQuery.data : myTeamsQuery.data) ?? []
  const classColumn = table.getColumn(CLASS_FILTER_COLUMN)
  const gradeColumn = table.getColumn(GRADE_FILTER_COLUMN)

  const classFilter = (classColumn?.getFilterValue() as string | undefined) ?? ALL_VALUE
  const gradeFilter = (gradeColumn?.getFilterValue() as string | undefined) ?? ALL_VALUE
  const accessFilter = (table.getColumn('access')?.getFilterValue() as string | undefined) ?? ALL_VALUE

  /** Only a real class has an avatar — the "Semua" and "Tanpa kelas" filters are not classes. */
  const selectedClass = classes.find((team) => team.id === classFilter) ?? null

  const classFilterLabel = (() => {
    if (classFilter === ALL_VALUE) return 'Kelas: semua'
    if (classFilter === NO_SCHOOL_CLASS_ID) return 'Kelas: tanpa kelas'
    return selectedClass?.name ?? '-'
  })()

  const gradeFilterLabel = (() => {
    if (gradeFilter === ALL_VALUE) return 'Tingkat: semua'
    if (gradeFilter === NO_SCHOOL_GRADE_ID) return 'Tingkat: tanpa tingkat'
    return schoolGradeLabel(gradeFilter) ?? '-'
  })()

  return (
    <div className='srd:flex srd:flex-wrap srd:items-center srd:justify-between srd:gap-3'>
      <div className='srd:flex srd:flex-1 srd:flex-wrap srd:items-center srd:gap-3'>
        {showClassFilter && (
          <Select
            value={classFilter}
            onValueChange={(value) => classColumn?.setFilterValue(value === ALL_VALUE ? undefined : value)}
          >
            <SelectTrigger className='srd:w-full srd:max-w-[200px]'>
              {/* The trigger reads as a filter state when nothing is picked — hence the "Kelas: "
                  prefix on the unfiltered and "tanpa kelas" cases. A picked class shows its own
                  name bare, since the avatar already says what the value is. */}
              <SelectValue>
                {() =>
                  selectedClass ? (
                    <>
                      <SchoolClassAvatar name={selectedClass.name} image={selectedClass.image} />
                      <span className='srd:truncate'>{selectedClass.name}</span>
                    </>
                  ) : (
                    classFilterLabel
                  )
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>Semua</SelectItem>
              {isAdminOrOwner && <SelectItem value={NO_SCHOOL_CLASS_ID}>Tanpa kelas</SelectItem>}

              {classes.length > 0 && <SelectSeparator />}

              {classes.map((team) => (
                <SelectItem key={team.id} value={team.id} className='srd:py-1.5'>
                  <SchoolClassAvatar name={team.name} image={team.image} />
                  <span className='srd:truncate'>{team.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {filters.includes('schoolGrade') && (
          <Select
            value={gradeFilter}
            onValueChange={(value) => gradeColumn?.setFilterValue(value === ALL_VALUE ? undefined : value)}
          >
            <SelectTrigger className='srd:w-full srd:max-w-[180px]'>
              <SelectValue>{() => gradeFilterLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>Semua</SelectItem>
              <SelectItem value={NO_SCHOOL_GRADE_ID}>Tanpa tingkat</SelectItem>

              <SelectSeparator />

              {SCHOOL_GRADE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {filters.includes('access') && (
          <Select
            value={accessFilter}
            onValueChange={(value) =>
              table.getColumn('access')?.setFilterValue(value === ALL_VALUE ? undefined : value)
            }
          >
            <SelectTrigger className='srd:w-full srd:max-w-[180px]'>
              <SelectValue>
                {(value: string | null) =>
                  value === 'limited' || value === 'premium' ? `Akses: ${ACCESS_LABEL[value]}` : 'Akses: semua'
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>Semua</SelectItem>
              <SelectSeparator />
              <SelectItem value='limited'>{ACCESS_LABEL.limited}</SelectItem>
              <SelectItem value='premium'>{ACCESS_LABEL.premium}</SelectItem>
            </SelectContent>
          </Select>
        )}

        <InputGroup className='srd:w-full srd:max-w-md'>
          <InputGroupAddon align='inline-start'>
            <Search className='srd:text-blue-100 srd:size-4' />
          </InputGroupAddon>

          <InputGroupInput
            placeholder={searchPlaceholder}
            value={(table.getColumn('name')?.getFilterValue() as string | undefined) ?? ''}
            onChange={(event) => table.getColumn('name')?.setFilterValue(event.target.value)}
          />
        </InputGroup>

        {actions}
      </div>
    </div>
  )
}
